import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { OperationType } from "@boosted/shared/bajie";

const API = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

export interface CabinetRow {
  id: string;
  shopId: string | null;
  type: string | null;
  online: boolean;
  signal: string | null;
  totalCount: number | null;
  rentableCount: number | null;
  returnableCount: number | null;
  lastSeenAt: number | null;
  updatedAt: number | null;
}

export interface BatteryRow {
  id: string;
  cabinetId: string | null;
  slot: number | null;
  vol: number | null;
  infoStatus: string | null;
  faultType: number | null;
  faultCause: string | null;
  lastSeenAt: number | null;
}

export interface EventRow {
  id: number;
  cabinetId: string;
  event: string;
  payload: unknown;
  timestamp: string;
  receivedAt: number;
}

export type StatsWindow = "24h" | "all";

export interface Stats {
  cabinets: { total: number; online: number };
  batteries: { total: number; faulty: number };
  events: {
    total: number;
    window: StatsWindow;
    rentals: number;
    returns: number;
    problems: number;
    last5min: number;
  };
  activeRentals: {
    count: number;
    longestOpenMin: number;
  };
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(API + path, { credentials: "include" });
  if (!res.ok) throw new Error(`${path} → ${res.status}`);
  const json = (await res.json()) as { data?: T } & T;
  return (json.data ?? json) as T;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(API + path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`${path} → ${res.status}: ${t}`);
  }
  return (await res.json()) as T;
}

export function useStats(window: StatsWindow = "24h") {
  return useQuery({
    queryKey: ["stats", window],
    queryFn: () => getJson<Stats>(`/stats?window=${window}`),
  });
}

export function useCabinets() {
  return useQuery({
    queryKey: ["cabinets"],
    queryFn: () => getJson<CabinetRow[]>("/cabinets"),
  });
}

export function useCabinet(id: string | null) {
  return useQuery({
    enabled: !!id,
    queryKey: ["cabinet", id],
    queryFn: () =>
      getJson<{ cabinet: CabinetRow; batteries: BatteryRow[] }>(`/cabinets/${id}`),
  });
}

export function useEvents(limit = 20) {
  return useQuery({
    queryKey: ["events", limit],
    queryFn: () => getJson<EventRow[]>(`/events?limit=${limit}`),
  });
}

export interface ActiveRental {
  batteryId: string;
  cabinetId: string;
  borrowedAt: number;
}

export interface RotationRow {
  batteryId: string;
  rotations: number;
  lastRentedAt: number;
}

export function useActiveRentals() {
  return useQuery({
    queryKey: ["rentals", "active"],
    queryFn: () => getJson<ActiveRental[]>("/rentals/active"),
  });
}

export function useBatteryRotations(limit = 50) {
  return useQuery({
    queryKey: ["batteries", "rotations", limit],
    queryFn: () => getJson<RotationRow[]>(`/batteries/rotations?limit=${limit}`),
  });
}

export function useSyncFromBajie() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => postJson<{ mode: string; count?: number; upserted?: number }>("/sync", {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cabinets"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export interface BackfillResult {
  pagesPulled: number;
  totalOrders: number;
  synthesizedBorrows: number;
  synthesizedReturns: number;
}

export function useBackfillOrders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => postJson<BackfillResult>("/orders/backfill", {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["batteries"] });
      qc.invalidateQueries({ queryKey: ["rentals"] });
    },
  });
}

export function useRefreshById() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getJson<unknown>(`/cabinets/${id}/refresh`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cabinets"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export interface ShopRevenueRow {
  shopId: string;
  shopName: string | null;
  rentals: number;
  revenue: number;
  avgDurationMin: number;
  lastRentalAt: string;
}

export function useShopRevenue(days?: number) {
  const qs = days ? `?days=${days}` : "";
  return useQuery({
    queryKey: ["shops", "revenue", days ?? "all"],
    queryFn: async () => {
      const res = await fetch(`${API}/shops/revenue${qs}`, { credentials: "include" });
      if (!res.ok) throw new Error(`/shops/revenue → ${res.status}`);
      return (await res.json()) as {
        data: ShopRevenueRow[];
        meta: { totalRevenue: number; totalRentals: number; days: number | null };
      };
    },
  });
}

export interface UptimeRow {
  cabinetId: string;
  samples: number;
  onlinePct: number;
  since: number;
  until: number;
}

export function useFleetUptime(days = 7) {
  return useQuery({
    queryKey: ["uptime", days],
    queryFn: () => getJson<UptimeRow[]>(`/uptime/fleet?days=${days}`),
  });
}

export interface LifecycleEvent {
  id: number;
  event: string;
  cabinetId: string;
  timestamp: string;
  receivedAt: number;
  slot: number | null;
  vol: number | null;
  faultType: number | null;
  faultCause: string | null;
}

export interface LifecycleResponse {
  batteryId: string;
  events: LifecycleEvent[];
  stats: {
    totalEvents: number;
    totalRentals: number;
    totalReturns: number;
    avgRentalDurationMs: number | null;
    currentlyOut: boolean;
  };
}

export function useBatteryLifecycle(id: string | null) {
  return useQuery({
    enabled: !!id,
    queryKey: ["battery-lifecycle", id],
    queryFn: () => getJson<LifecycleResponse>(`/batteries/${id}/lifecycle`),
  });
}

export function useFaultyBatteries() {
  return useQuery({
    queryKey: ["batteries", "faulty"],
    queryFn: () => getJson<BatteryRow[]>("/batteries?fault=true"),
  });
}

export function useEjectRepair() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { batteryId: string; reason?: string }) =>
      postJson<unknown>(`/batteries/${input.batteryId}/eject-repair`, {
        reason: input.reason ?? "repair",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["batteries"] });
      qc.invalidateQueries({ queryKey: ["cabinets"] });
    },
  });
}

export function useRefreshCabinet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getJson<unknown>(`/cabinets/${id}/refresh`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["cabinet", id] });
      qc.invalidateQueries({ queryKey: ["cabinets"] });
    },
  });
}

export function useDeviceOperation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      cabinetId: string;
      operationType: OperationType;
      slotNum?: number;
      reason?: string;
    }) =>
      postJson<unknown>(`/cabinets/${input.cabinetId}/operation`, {
        operationType: input.operationType,
        slotNum: input.slotNum ?? 0,
        reason: input.reason ?? "",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cabinets"] });
      qc.invalidateQueries({ queryKey: ["events"] });
    },
  });
}
